import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config/constants';
import {
  Background,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  ConnectionLineType,
  useNodesState,
  useEdgesState,
  useStoreApi,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { generateElementsFromTree } from '../../utils/generateElements';
import { getLayoutedElements } from '../../utils/layout';
import { buildFileTree } from '../../utils/fileTree';
import UniversalNode from '../UniversalNode';
import FitViewHelper from '../FitViewHelper';
import { animationDuration } from '../../utils/layout';

const nodeTypes = {
  universal: UniversalNode,
};

// Inner component that uses React Flow hooks (must be inside ReactFlowProvider)
function FlowContent({ rootTree, onTreeRefresh, onOpenCodeView }) {
  // Helper function to find a node in the tree by id
  const findNodeInTree = useCallback((nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeInTree(node.children, id);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Helper function to count files recursively in a folder or file with children
  const countFilesInFolder = useCallback((node) => {
    if (!node || !node.children) return 0;
    let count = 0;
    node.children.forEach((child) => {
      if (child.type === 'file' || child.type === 'function') {
        count++;
      } else if (child.type === 'folder') {
        count += countFilesInFolder(child);
      }
    });
    return count;
  }, []);

  // Generate initial nodes and edges from tree
  const { initialNodes, initialEdges } = useMemo(() => {
    if (!rootTree || rootTree.length === 0) {
      return { initialNodes: [], initialEdges: [] };
    }

    try {
      const { nodes, edges } = generateElementsFromTree(rootTree);
      return { initialNodes: nodes, initialEdges: edges };
    } catch (error) {
      console.error('Error generating elements:', error);
      return { initialNodes: [], initialEdges: [] };
    }
  }, [rootTree]);

  // Save all files to localStorage for @ mentions
  useEffect(() => {
    if (initialNodes.length > 0) {
      const filesForMention = initialNodes.map(node => ({
        id: node.id,
        label: node.data?.label,
        path: node.data?.path,
        type: node.type
      }));
      localStorage.setItem('availableFiles', JSON.stringify(filesForMention));
      window.dispatchEvent(new Event('availableFilesUpdated'));
    }
  }, [initialNodes]);

  // Initialize collapse states - all folders start expanded (empty state)
  const [collapseStates, setCollapseStates] = useState({});
  
  // Track if collapseAll has been called on boot
  const hasCollapsedOnBootRef = useRef(false);

  // Layout initial elements
  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
    if (initialNodes.length === 0) {
      return { nodes: [], edges: [] };
    }
    return getLayoutedElements(initialNodes, initialEdges);
  }, [initialNodes, initialEdges]);

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);
  
  // Track connecting node for drag-to-create
  const connectingNodeId = useRef(null);
  const store = useStoreApi();
  const { screenToFlowPosition, getInternalNode, fitView } = useReactFlow();
  
  // Minimum distance for proximity connection (in pixels)
  const MIN_DISTANCE = 150;

  // Generic toggle function for folders
  const toggleNode = useCallback((nodeId) => {
    setCollapseStates((prev) => ({
      ...prev,
      [nodeId]: !(prev[nodeId] || false),
    }));
  }, []);

  // Get all descendant nodes (children, grandchildren, etc.) of a given parent node
  const getAllDescendants = useCallback((parentId, allEdges, visited = new Set()) => {
    // Avoid infinite loops if there are cycles
    if (visited.has(parentId)) {
      return new Set();
    }
    visited.add(parentId);

    const descendants = new Set();
    const childEdges = allEdges.filter((edge) => edge.source === parentId);
    
    childEdges.forEach((edge) => {
      const childId = edge.target;
      descendants.add(childId);
      
      // Recursively get descendants of this child
      const childDescendants = getAllDescendants(childId, allEdges, visited);
      childDescendants.forEach((descId) => descendants.add(descId));
    });

    return descendants;
  }, []);

  // Find the closest folder node within proximity distance
  const getClosestFolderNode = useCallback((draggedNode) => {
    // Only allow nodes without children to connect to nodes with children
    const draggedHasChildren = draggedNode?.data?.hasChildren || (draggedNode?.data?.children && draggedNode.data.children.length > 0);
    
    if (draggedHasChildren) {
      return null;
    }

    const { nodeLookup } = store.getState();
    const internalNode = getInternalNode(draggedNode.id);
    
    if (!internalNode) {
      return null;
    }

    const closestFolder = Array.from(nodeLookup.values()).reduce(
      (res, n) => {
        // Only consider nodes with children (folder-like nodes)
        const hasChildren = n.data?.hasChildren || (n.data?.children && n.data.children.length > 0);
        if (n.id !== internalNode.id && hasChildren) {
          const folderInternal = getInternalNode(n.id);
          if (!folderInternal) {
            return res;
          }

          const dx =
            folderInternal.internals.positionAbsolute.x -
            internalNode.internals.positionAbsolute.x;
          const dy =
            folderInternal.internals.positionAbsolute.y -
            internalNode.internals.positionAbsolute.y;
          const d = Math.sqrt(dx * dx + dy * dy);

          if (d < res.distance && d < MIN_DISTANCE) {
            res.distance = d;
            res.node = n;
          }
        }

        return res;
      },
      {
        distance: Number.MAX_VALUE,
        node: null,
      },
    );

    if (!closestFolder.node) {
      return null;
    }

    // File is source, folder is target
    return {
      id: `${draggedNode.id}-${closestFolder.node.id}`,
      source: draggedNode.id,
      target: closestFolder.node.id,
    };
  }, [store, getInternalNode]);

  // Delete file or folder on filesystem and refresh tree
  const deleteNode = useCallback(async (nodePath) => {
    if (!nodePath) {
      console.error('Missing path for deletion');
      return false;
    }

    try {
      // Remove root folder from path - server expects relative path without root
      // e.g., "codemaps/static/js" -> "static/js" or "codemaps/src" -> "src"
      const pathParts = nodePath.split('/');
      const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : nodePath;

      const response = await fetch('${API_BASE_URL}/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify({
          path: relativePath,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to delete: ${response.status}`);
      }

      // Refresh the file tree
      if (onTreeRefresh) {
        onTreeRefresh();
      }

      return true;
    } catch (error) {
      console.error('Error deleting node:', error);
      alert(`Failed to delete: ${error.message}`);
      return false;
    }
  }, [onTreeRefresh]);

  // Rename file on filesystem and refresh tree
  const renameNode = useCallback(async (nodePath, newFileName) => {
    if (!nodePath || !newFileName) {
      console.error('Missing path or new filename for rename');
      return false;
    }

    try {
      // Extract directory path and construct new path
      const pathParts = nodePath.split('/');
      const fileName = pathParts.pop();
      const directoryPath = pathParts.join('/');
      const newFilePath = directoryPath ? `${directoryPath}/${newFileName}` : newFileName;

      // Remove root folder from paths - server expects relative paths without root
      // e.g., "codemaps/static/js" -> "static/js" or "codemaps/src" -> "src"
      const sourcePathParts = nodePath.split('/');
      const relativeSourcePath = sourcePathParts.length > 1 ? sourcePathParts.slice(1).join('/') : nodePath;
      
      const destinationPathParts = newFilePath.split('/');
      const relativeDestinationPath = destinationPathParts.length > 1 ? destinationPathParts.slice(1).join('/') : newFilePath;

      const response = await fetch('${API_BASE_URL}/move', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify({
          source: relativeSourcePath,
          destination: relativeDestinationPath,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to rename: ${response.status}`);
      }

      // Refresh the file tree
      if (onTreeRefresh) {
        onTreeRefresh();
      }

      return true;
    } catch (error) {
      console.error('Error renaming node:', error);
      alert(`Failed to rename: ${error.message}`);
      return false;
    }
  }, [onTreeRefresh]);

  // Move file to folder on filesystem and refresh tree
  const moveFileToFolder = useCallback(async (fileNode, folderNode) => {
    if (!fileNode.data?.path || !folderNode.data?.path) {
      console.error('Missing path data for file or folder');
      return false;
    }

    const filePath = fileNode.data.path;
    const folderPath = folderNode.data.path;
    
    // Construct new file path: folderPath/fileName
    const fileName = filePath.split('/').pop();
    const newFilePath = `${folderPath}/${fileName}`;

    // Remove root folder from paths - server expects relative path without root
    // e.g., "codemaps/static/js" -> "static/js" or "codemaps/src" -> "src"
    const filePathParts = filePath.split('/');
    const relativeSourcePath = filePathParts.length > 1 ? filePathParts.slice(1).join('/') : filePath;
    
    const newFilePathParts = newFilePath.split('/');
    const relativeDestinationPath = newFilePathParts.length > 1 ? newFilePathParts.slice(1).join('/') : newFilePath;

    try {
      // Call move API endpoint with source and destination parameters
      const response = await fetch('${API_BASE_URL}/move', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify({
          source: relativeSourcePath,
          destination: relativeDestinationPath,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to move file: ${response.status}`);
      }

      // Refresh the file tree by fetching updated file list
      if (onTreeRefresh) {
        onTreeRefresh();
      } else {
        // Fallback: fetch file list and rebuild tree
        const listResponse = await fetch('${API_BASE_URL}/list', {
          method: 'GET',
          mode: 'cors'
        });
        
        if (listResponse.ok) {
          const contentType = listResponse.headers.get('content-type');
          let fileArray = [];
          
          if (contentType && contentType.includes('application/json')) {
            const data = await listResponse.json();
            fileArray = data.files || data || [];
          } else {
            const text = await listResponse.text();
            try {
              const data = JSON.parse(text);
              fileArray = data.files || data || [];
            } catch (e) {
              fileArray = text.trim().split('\n').filter(line => line.trim());
            }
          }
          
          // Filter ignored files (simplified - should use same logic as FileList)
          const filteredFiles = fileArray.filter(path => 
            !path.includes('node_modules') && 
            !path.includes('.git') &&
            !path.includes('.DS_Store')
          );
          
          const newTree = buildFileTree(filteredFiles);
          // Note: We can't update rootTree directly here, but the refresh callback should handle it
        }
      }

      return true;
    } catch (error) {
      console.error('Error moving file:', error);
      alert(`Failed to move file: ${error.message}`);
      return false;
    }
  }, [onTreeRefresh]);

  // Update nodes and edges when collapse state changes
  useEffect(() => {
    if (initialNodes.length === 0) {
      return;
    }

    // Get current nodes and edges from the store to preserve dynamic nodes
    const { nodeLookup, edgeLookup } = store.getState();
    const currentNodes = Array.from(nodeLookup.values());
    const currentEdges = Array.from(edgeLookup.values());

    // Extract dynamic nodes (NewNodes and other dynamically added nodes)
    const dynamicNodes = currentNodes.filter((node) => node.type === 'new');
    const dynamicNodeIds = new Set(dynamicNodes.map((n) => n.id));
    
    // Extract edges connected to dynamic nodes
    const dynamicEdges = currentEdges.filter(
      (edge) => dynamicNodeIds.has(edge.source) || dynamicNodeIds.has(edge.target)
    );

    let nodesToShow = initialNodes.map((node) => ({ ...node }));
    let edgesToShow = initialEdges.map((edge) => ({ ...edge }));

    // Update nodes to use universal type - all nodes can have children
    nodesToShow = nodesToShow.map((node) => {
      const hasChildren = node.data.hasChildren || (node.data.children && node.data.children.length > 0);
      
      // Check if children are all functions/methods/classes (then treat as file) or files/folders (then treat as folder)
      const children = node.data.children || [];
      const childrenAreFunctionsMethodsOrClasses = hasChildren && Array.isArray(children) && children.every(child => {
        const childType = child.type || (child.originalNode && child.originalNode.type);
        return childType === 'function' || childType === 'method' || childType === 'class';
      });
      // Check if this node is a class
      const originalNodeType = node.data.originalNode?.type;
      const isClassFromClassName = typeof node.className === 'string' && node.className.includes('class-node');
      const isClass = originalNodeType === 'class' || isClassFromClassName;
      const isFolder = hasChildren && !childrenAreFunctionsMethodsOrClasses && !isClass;
      
      const isCollapsed = collapseStates[node.id] !== undefined ? collapseStates[node.id] : (hasChildren ? true : false);
      
      return {
        ...node,
        type: 'universal',
        className: node.className, // Preserve className (class-node, method-node, function-node, folder-node)
        selectable: true, // All nodes are selectable (including folders, classes, and files with children)
        data: {
          ...node.data,
          isCollapsed: isCollapsed,
          // Both folders, classes, and files with function/method children toggle
          onToggle: hasChildren ? toggleNode : undefined,
        },
      };
    });

    // Hide all descendant nodes (children, grandchildren, etc.) for all collapsed nodes
    const collapsedNodeIds = Object.keys(collapseStates).filter(
      (nodeId) => collapseStates[nodeId] === true
    );
    
    const allDescendantNodeIds = new Set();
    collapsedNodeIds.forEach((parentId) => {
      const descendantIds = getAllDescendants(parentId, edgesToShow);
      descendantIds.forEach((descId) => allDescendantNodeIds.add(descId));
    });

    if (allDescendantNodeIds.size > 0) {
      // Hide all descendant nodes
      nodesToShow = nodesToShow.filter((node) => !allDescendantNodeIds.has(node.id));
      
      // Hide edges connected to hidden nodes
      edgesToShow = edgesToShow.filter(
        (edge) => !allDescendantNodeIds.has(edge.source) && !allDescendantNodeIds.has(edge.target)
      );
    }

    // Recalculate layout with filtered nodes and edges
    const layouted = getLayoutedElements(nodesToShow, edgesToShow);

    // Calculate which nodes have incoming/outgoing connections based on final edges
    const nodeConnections = {};
    layouted.nodes.forEach((node) => {
      nodeConnections[node.id] = {
        hasIncoming: layouted.edges.some((edge) => edge.target === node.id),
        hasOutgoing: layouted.edges.some((edge) => edge.source === node.id),
      };
    });

    // Add connection info to node data and click handlers
    const finalNodes = layouted.nodes.map((node) => {
      const originalNode = findNodeInTree(rootTree, node.id);
      const hasChildren = node.data.hasChildren || (node.data.children && node.data.children.length > 0);
      const fileCount = hasChildren && originalNode ? countFilesInFolder(originalNode) : 0;
      return {
        ...node,
        data: {
          ...node.data,
          hasIncoming: nodeConnections[node.id]?.hasIncoming || false,
          hasOutgoing: nodeConnections[node.id]?.hasOutgoing || false,
          originalNode: originalNode, // Store original node for click handlers
          fileCount: fileCount, // Add file count for folder nodes
          onDelete: deleteNode, // Add delete handler
          onRename: renameNode, // Add rename handler
          onOpenCodeView: onOpenCodeView, // Add code view handler
        },
      };
    });

    // Merge preserved dynamic nodes back into final nodes
    const allNodes = [...finalNodes, ...dynamicNodes];
    const allEdges = [...layouted.edges, ...dynamicEdges];

    setNodes(allNodes);
    setEdges(allEdges);
  }, [collapseStates, toggleNode, getAllDescendants, setNodes, setEdges, initialNodes, initialEdges, rootTree, findNodeInTree, countFilesInFolder, store, deleteNode, renameNode, onOpenCodeView]);

  const onConnect = useCallback(
    async (params) => {
      const { source, target } = params;
      
      // Find source and target nodes
      const sourceNode = nodes.find((n) => n.id === source);
      const targetNode = nodes.find((n) => n.id === target);
      
      // Check if source has children (folder-like) and target doesn't (file-like)
      const sourceHasChildren = sourceNode && (sourceNode.data?.hasChildren || (sourceNode.data?.children && sourceNode.data.children.length > 0));
      const targetHasChildren = targetNode && (targetNode.data?.hasChildren || (targetNode.data?.children && targetNode.data.children.length > 0));
      
      // If folder-like node connecting to file-like node, move the file to the folder
      if (sourceHasChildren && !targetHasChildren && sourceNode && targetNode) {
        const moveSuccess = await moveFileToFolder(targetNode, sourceNode);
        if (!moveSuccess) {
          // Don't add the edge if move failed
          return;
        }
      }
      
      // Add the edge
      setEdges((eds) =>
        addEdge({ ...params, type: ConnectionLineType.Step, animated: true }, eds),
      );
    },
    [setEdges, nodes, moveFileToFolder],
  );

  // Handle connect start - remember which node we're dragging from
  const onConnectStart = useCallback((_, { nodeId }) => {
    connectingNodeId.current = nodeId;
  }, []);

  // Helper function to get child node position
  const getChildNodePosition = useCallback((event) => {
    if (!screenToFlowPosition) {
      return null;
    }

    // Convert screen coordinates to React Flow coordinates
    const panePosition = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    return panePosition;
  }, [screenToFlowPosition]);

  // Create folder on filesystem
  const createFolderOnFilesystem = useCallback(async (folderPath) => {
    try {
      // Create a .gitkeep file inside the folder to create the directory structure
      const response = await fetch('${API_BASE_URL}/write', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify({
          path: folderPath,
          content: '', // Empty file to create the folder
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create folder: ${response.status} ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error;
    }
  }, []);

  // Create Python file on filesystem
  const createPythonFileOnFilesystem = useCallback(async (filePath, content = '') => {
    try {
      const response = await fetch('${API_BASE_URL}/write', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify({
          path: filePath,
          content: content,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create Python file: ${response.status} ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error('Error creating Python file:', error);
      throw error;
    }
  }, []);

  // Create directory on filesystem
  const createDirectoryOnFilesystem = useCallback(async (dirPath) => {
    try {
      const response = await fetch('${API_BASE_URL}/write_dir', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify({
          path: dirPath,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create directory: ${response.status} ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error('Error creating directory:', error);
      throw error;
    }
  }, []);

  // Helper to check if a filename has an extension
  const hasExtension = useCallback((fileName) => {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot !== -1 && lastDot !== fileName.length - 1 && lastDot > 0;
  }, []);

  // Create file or folder from NewNode input
  const createFileFromNewNode = useCallback(async (newNodeId, fileName, parentNode, position) => {
    if (!parentNode.data?.path) {
      console.error('Parent node missing path data');
      return;
    }

    const folderPath = parentNode.data.path;
    const isFolder = !hasExtension(fileName);
    
    // Construct full path for node storage: folderPath/fileName
    const fullPath = folderPath ? `${folderPath}/${fileName}` : fileName;
    
    // Remove root folder from path - server expects relative path without root
    // e.g., "codemaps/static/js" -> "static/js" or "codemaps/src" -> "src"
    const pathParts = folderPath.split('/');
    const relativeFolderPath = pathParts.length > 1 ? pathParts.slice(1).join('/') : folderPath;
    const relativePath = relativeFolderPath ? `${relativeFolderPath}/${fileName}` : fileName;
    
    try {
      if (isFolder) {
        // Create directory on filesystem using relative path (without root folder)
        await createDirectoryOnFilesystem(relativePath);
        
        // Generate new node ID (using full path as ID to match existing pattern)
        const folderNodeId = fullPath;
        
        // Remove the NewNode and its edge
        setNodes((nds) => nds.filter((n) => n.id !== newNodeId));
        setEdges((eds) => eds.filter((e) => e.target !== newNodeId));
        
        // Create new folder node
        const newNode = {
          id: folderNodeId,
          type: 'folder',
          data: {
            label: fileName,
            isFolder: true,
            path: fullPath,
            isCollapsed: false,
            onToggle: toggleNode,
            hasIncoming: true,
            hasOutgoing: false,
            fileCount: 0,
            onDelete: deleteNode,
          },
          position: position || { x: 0, y: 0 },
        };

        // Create new edge connecting parent to new folder
        const newEdge = {
          id: `e${parentNode.id}-${folderNodeId}`,
          source: parentNode.id,
          target: folderNodeId,
          type: 'step',
        };

        // Add folder node and edge
        setNodes((nds) => [...nds, newNode]);
        setEdges((eds) => [...eds, newEdge]);
      } else {
        // Create file on filesystem using relative path (without root folder)
        await createPythonFileOnFilesystem(relativePath, '');
        
        // Generate new node ID (using full path as ID to match existing pattern)
        const fileNodeId = fullPath;
        
        // Remove the NewNode and its edge
        setNodes((nds) => nds.filter((n) => n.id !== newNodeId));
        setEdges((eds) => eds.filter((e) => e.target !== newNodeId));
        
        // Create new file node with the stored position
        const newNode = {
          id: fileNodeId,
          type: 'file',
          data: {
            label: fileName,
            isFolder: false,
            path: fullPath, // Store full path (with root) for consistency with other nodes
            hasIncoming: true,
            hasOutgoing: false,
            onDelete: deleteNode,
          },
          position: position || { x: 0, y: 0 },
        };

        // Create new edge connecting parent to new file
        const newEdge = {
          id: `e${parentNode.id}-${fileNodeId}`,
          source: parentNode.id,
          target: fileNodeId,
          type: 'step',
        };

        // Add file node and edge
        setNodes((nds) => [...nds, newNode]);
        setEdges((eds) => [...eds, newEdge]);
      }
      
      // Refresh the file tree to pick up the new file/folder
      if (onTreeRefresh) {
        onTreeRefresh();
      }
    } catch (error) {
      console.error('Error creating file/folder:', error);
      alert(`Failed to create ${isFolder ? 'folder' : 'file'}: ${error.message}`);
    }
  }, [createPythonFileOnFilesystem, createDirectoryOnFilesystem, toggleNode, setNodes, setEdges, onTreeRefresh, hasExtension, deleteNode]);

  // Add new file node (NewNode with input)
  const addChildNewFileNode = useCallback((parentNode, position) => {
    if (!parentNode.data?.path) {
      console.error('Parent node missing path data');
      return;
    }

    // Generate unique ID for the NewNode
    const newNodeId = `new-${Date.now()}`;
    
    // Create NewNode
    const newNode = {
      id: newNodeId,
      type: 'new',
      data: {
        label: '',
        isFolder: false,
        hasIncoming: true,
        hasOutgoing: false,
        onCreateFile: (fileName) => {
          createFileFromNewNode(newNodeId, fileName, parentNode, position);
        },
        onCreateFolder: (folderName) => {
          createFileFromNewNode(newNodeId, folderName, parentNode, position);
        },
        onCancel: () => {
          // Remove the NewNode and its edge
          setNodes((nds) => nds.filter((n) => n.id !== newNodeId));
          setEdges((eds) => eds.filter((e) => e.target !== newNodeId));
        },
      },
      position,
    };

    // Create new edge connecting parent to NewNode
    const newEdge = {
      id: `e${parentNode.id}-${newNodeId}`,
      source: parentNode.id,
      target: newNodeId,
      type: 'step',
    };

    // Add NewNode and edge
    setNodes((nds) => [...nds, newNode]);
    setEdges((eds) => [...eds, newEdge]);
  }, [setNodes, setEdges, createFileFromNewNode]);

  // Handle node drag - show temporary edge when near folder
  const onNodeDrag = useCallback(
    (_, node) => {
      const closeEdge = getClosestFolderNode(node);

      setEdges((es) => {
        // Remove any existing temp edges
        const nextEdges = es.filter((e) => e.className !== 'temp');

        if (
          closeEdge &&
          !nextEdges.find(
            (ne) =>
              ne.source === closeEdge.source && ne.target === closeEdge.target,
          )
        ) {
          // Add temporary edge with dashed styling
          closeEdge.className = 'temp';
          closeEdge.type = 'step';
          nextEdges.push(closeEdge);
        }

        return nextEdges;
      });
    },
    [getClosestFolderNode, setEdges],
  );

  // Handle node drag stop - move file if connected to folder
  const onNodeDragStop = useCallback(
    async (_, node) => {
      const closeEdge = getClosestFolderNode(node);

      // Remove temp edges
      setEdges((es) => es.filter((e) => e.className !== 'temp'));

      if (closeEdge) {
        // Find the folder node
        const folderNode = nodes.find((n) => n.id === closeEdge.target);
        
        const nodeHasChildren = node.data?.hasChildren || (node.data?.children && node.data.children.length > 0);
        const folderHasChildren = folderNode.data?.hasChildren || (folderNode.data?.children && folderNode.data.children.length > 0);
        if (folderNode && !nodeHasChildren && folderHasChildren) {
          // Move the file-like node to the folder-like node
          await moveFileToFolder(node, folderNode);
        }
      }
    },
    [getClosestFolderNode, setEdges, nodes, moveFileToFolder],
  );

  // Handle connect end - create new file node (NewNode) if dragging from FolderNode handle
  const onConnectEnd = useCallback(
    (event) => {
      const { nodeLookup } = store.getState();
      const targetIsPane = event.target && event.target.classList && event.target.classList.contains('react-flow__pane');

      if (targetIsPane && connectingNodeId.current) {
        const parentNode = nodeLookup.get(connectingNodeId.current);
        
        // Create NewNode if dragging from a node with children (folder-like)
        const parentHasChildren = parentNode?.data?.hasChildren || (parentNode?.data?.children && parentNode.data.children.length > 0);
        if (parentNode && parentHasChildren) {
          const childNodePosition = getChildNodePosition(event);
          
          if (childNodePosition) {
            addChildNewFileNode(parentNode, childNodePosition);
          }
        }
      }
      
      // Reset connecting node
      connectingNodeId.current = null;
    },
    [store, getChildNodePosition, addChildNewFileNode],
  );

  // Handle node click
  const onNodeClick = useCallback((event, node) => {
    if (!node.data) return;
    
    const hasChildren = node.data.hasChildren || (node.data.children && node.data.children.length > 0);
    
    // Toggle for nodes with children (both folders and files with function children)
    if (hasChildren && node.data.onToggle) {
      node.data.onToggle(node.id);
    }
    // Nodes without children are handled by React Flow selection - clicking selects them and shows the menu
  }, []);


  // Track when initial collapse is complete
  const [isInitialLayoutReady, setIsInitialLayoutReady] = useState(false);

  // Recenter the graph view
  const handleRecenter = useCallback(() => {
    fitView({ padding: 0.1, duration: animationDuration });
  }, [fitView]);


  // Collapse all nodes with children on boot (once)
  useEffect(() => {
    if (!hasCollapsedOnBootRef.current && initialNodes.length > 0) {
      const allFolderStates = {};
      initialNodes.forEach((node) => {
        const hasChildren = node.data.hasChildren || (node.data.children && node.data.children.length > 0);
        if (hasChildren) {
          allFolderStates[node.id] = true;
        }
      });
      if (Object.keys(allFolderStates).length > 0) {
        setCollapseStates(allFolderStates);
        hasCollapsedOnBootRef.current = true;
        // Signal that initial layout is ready after collapse state is set
        // Use timeout to ensure layout effect has run
        setTimeout(() => setIsInitialLayoutReady(true), 50);
      } else {
        // No folders to collapse, layout is ready immediately
        setIsInitialLayoutReady(true);
      }
    }
  }, [initialNodes]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      background: '#fafafa',
      position: 'relative'
    }}>
      {/* Recenter button */}
      <button
        onClick={handleRecenter}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          zIndex: 10,
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          borderRadius: '8px',
          padding: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.4)';
        }}
        title="Recenter view (⌘C)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="rgba(0,0,0,0.5)" style={{ width: '18px', height: '18px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
        </svg>
      </button>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        onNodeClick={onNodeClick}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        connectionLineType={ConnectionLineType.Step}
        nodeTypes={nodeTypes}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <FitViewHelper nodes={nodes} edges={edges} isReady={isInitialLayoutReady} />
      </ReactFlow>
    </div>
  );
}

// Outer wrapper component that provides ReactFlowProvider
function ElementsGenerator({ rootTree, onTreeRefresh, onOpenCodeView }) {
  if (!rootTree || rootTree.length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: '#3b82f6',
        fontSize: '14px',
        fontFamily: 'monospace',
        background: '#fafafa',
        border: '1px solid #ddd',
        borderRadius: '4px'
      }}>
        No file tree available - no files detected in repo.
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <FlowContent
        rootTree={rootTree}
        onTreeRefresh={onTreeRefresh}
        onOpenCodeView={onOpenCodeView}
      />
    </ReactFlowProvider>
  );
}

export default ElementsGenerator;
